import { Check } from 'lucide-react';
import { FC } from 'react';

interface Plan {
  title: string;
  price: string;
  description: string;
  features: string[];
  isPopular?: boolean;
}

const Pricing: FC = () => {
  const plans: Plan[] = [
    {
      title: 'Basic',
      price: '$9.99',
      description: 'Perfect for new job seekers',
      features: [
        '3 templates',
        'Basic ATS check',
        'Unlimited PDF exports',
        'Email support',
      ],
    },
    {
      title: 'Pro',
      price: '$19.99',
      description: 'Ideal for active job hunters',
      features: [
        '10 premium templates',
        'Advanced ATS optimization',
        'AI suggestions',
        'Cover letter builder',
        'Priority support',
      ],
      isPopular: true,
    },
    {
      title: 'Premium',
      price: '$29.99',
      description: 'For career professionals',
      features: [
        'All templates',
        'Expert ATS optimization',
        'Advanced AI enhancement',
        'LinkedIn optimization',
        '1-on-1 review',
      ],
    },
  ];

  return (
    <section id='pricing' className='py-24 bg-background'>
      <div className='container mx-auto px-4'>
        <div className='text-center max-w-2xl mx-auto mb-16'>
          <div className='inline-flex items-center space-x-2 bg-accent text-accent-foreground px-4 py-2 rounded-md text-sm font-medium mb-4'>
            <span>Affordable Plans</span>
          </div>
          <h2 className='text-3xl lg:text-4xl font-bold mb-4 text-foreground'>
            Choose Your Career Plan
          </h2>
          <p className='text-lg text-muted-foreground'>
            Invest in your future with premium tools.
          </p>
        </div>
        <div className='grid md:grid-cols-3 gap-8 max-w-5xl mx-auto'>
          {plans.map((plan, index) => (
            <div
              key={plan.title}
              className={`bg-card border rounded-xl overflow-hidden transition-smooth hover:shadow-xl animate-fadeInUp ${
                plan.isPopular ? 'border-2 border-primary' : 'border-border'
              }`}
              style={{ animationDelay: `${0.1 * (index + 1)}s` }}>
              {plan.isPopular && (
                <div className='bg-primary text-primary-foreground text-center py-1 text-xs font-medium'>
                  MOST POPULAR
                </div>
              )}
              <div className='p-6 border-b border-border'>
                <h3 className='text-xl font-bold mb-2 text-card-foreground'>
                  {plan.title}
                </h3>
                <div className='flex items-end mb-4'>
                  <span className='text-4xl font-bold text-card-foreground'>
                    {plan.price}
                  </span>
                  <span className='text-muted-foreground ml-2 mb-1'>/month</span>
                </div>
                <p className='text-sm text-muted-foreground'>{plan.description}</p>
              </div>
              <div className='p-6'>
                <ul className='space-y-3'>
                  {plan.features.map((feature) => (
                    <li key={feature} className='flex items-start'>
                      <Check size={18} className='text-primary mt-0.5 mr-2' />
                      <span className='text-card-foreground'>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  className={`w-full mt-6 rounded-md px-4 py-3 text-sm font-medium transition-smooth ${
                    plan.isPopular
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
                  }`}>
                  Choose {plan.title}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
